/**
 * English strings of what playing earns (milestone 7): the rap sheet, levels, challenges, trophies,
 * cosmetics, ratings and leaderboards, and the summary at the end of a heist. `meta-it.ts` is typed
 * against this shape, so a missing or extra key is a compile error. The names of the trophies, the
 * cosmetics and the challenges are looked up by id (`packages/meta`): a test checks that every id
 * has its words.
 */
export const metaEn = {
  meta: {
    gate: {
      signIn: 'Sign in to see your progress',
      signInBody:
        'XP, the rap sheet, trophies and the leaderboards belong to an account (a guest account will do). Heists against bots in Solo do not count.',
      signInAction: 'Open the account',
      unavailable: 'Progress is not available in this version',
      unavailableBody: 'This build has no account service, so there is nowhere to keep it.',
      loading: 'Loading…',
      failed: 'The progress could not be loaded. Try again in a moment.',
      retry: 'Try again',
    },
    progress: {
      title: 'Rap sheet',
      level: 'Level {{level}}',
      xp: '{{xp}} XP',
      toNext: '{{needed}} XP to level {{next}}',
      levelBar: 'Progress to the next level',
      stats: 'The record',
      season: 'Season {{n}}',
      ratings: 'Ranked',
      ratingsHint: 'Your two ratings: as a thief and as the infiltrator.',
      placing: 'Placement: {{games}} of {{total}} games',
      games: '{{games}} games',
      challenges: 'Challenges',
      daily: 'Today',
      weekly: 'This week',
      done: 'Done',
      reward: '+{{xp}} XP',
      trophies: 'Trophies',
      trophiesCount: '{{owned}} of {{total}}',
      recent: 'Latest heists',
      recentEmpty: 'No heist online yet. Play a quick match or open a private room.',
      won: 'Won',
      lost: 'Lost',
      left: 'Left',
      asInfiltrator: 'as the Mole',
      empty: 'Nothing here yet: play a heist online and this fills up.',
      toCollection: 'Open the collection',
      toLeaderboards: 'Leaderboards',
      replay: 'Watch again',
    },
    stats: {
      matches: 'Heists played',
      heists: 'Heists brought home',
      wins: 'Wins',
      loot: 'Loot taken home',
      lootUnit: '{{amount}} M lire',
      bestLoot: 'Best share',
      asInfiltrator: 'Played as the Mole',
      infiltratorWins: 'Won as the Mole',
      unmasksRight: 'Moles unmasked',
      unmasksWrong: 'Wrong accusations',
      abandoned: 'Heists left',
      dailyRuns: 'Daily heists played',
    },
    leagues: {
      pickpocket: 'Pickpocket',
      thief: 'Thief',
      professional: 'Professional',
      master: 'Master',
      legend: 'Legend',
    },
    ratingKinds: { thief: 'As a thief', infiltrator: 'As the Mole' },
    modes: {
      quick: 'Quick match',
      private: 'Private room',
      ranked: 'Ranked',
      daily: 'Daily heist',
    },
    challenge: {
      // The count is the goal, so that one and many read right.
      matches_one: 'Play {{count}} heist',
      matches_other: 'Play {{count}} heists',
      heists_one: 'Bring home {{count}} heist',
      heists_other: 'Bring home {{count}} heists',
      wins_one: 'Win {{count}} heist',
      wins_other: 'Win {{count}} heists',
      loot_one: 'Take home {{count}} million lire',
      loot_other: 'Take home {{count}} million lire',
      unmasksRight_one: 'Unmask the Mole {{count}} time',
      unmasksRight_other: 'Unmask the Mole {{count}} times',
      infiltratorWins_one: 'Win {{count}} heist as the Mole',
      infiltratorWins_other: 'Win {{count}} heists as the Mole',
      cleanHeists_one: 'Bring home {{count}} heist with the alarm at 30 % or less',
      cleanHeists_other: 'Bring home {{count}} heists with the alarm at 30 % or less',
      dailyRuns_one: 'Play {{count}} daily heist',
      dailyRuns_other: 'Play {{count}} daily heists',
      role_one: 'Play {{count}} heist as the {{role}}',
      role_other: 'Play {{count}} heists as the {{role}}',
      map_one: 'Play {{count}} heist at {{map}}',
      map_other: 'Play {{count}} heists at {{map}}',
    },
    trophy: {
      first_heist: { name: 'First score', desc: 'Bring home your first heist.' },
      ten_heists: { name: 'Ten jobs', desc: 'Bring home 10 heists.' },
      fifty_heists: { name: 'Fifty jobs', desc: 'Bring home 50 heists.' },
      millionaire: { name: 'Lire billionaire', desc: 'Take home 1000 million lire in all.' },
      fat_share: { name: 'The lion’s share', desc: 'Take home 150 million lire in one heist.' },
      mole_hunter: { name: 'Mole hunter', desc: 'Name the real Mole 5 times.' },
      bloodhound: { name: 'Bloodhound', desc: 'Name the real Mole 25 times.' },
      double_agent: { name: 'Double agent', desc: 'Win 3 heists as the Mole.' },
      daily_regular: { name: 'Regular', desc: 'Play 7 daily heists.' },
      veteran: { name: 'Veteran', desc: 'Play 100 heists.' },
      clean_getaway: {
        name: 'Clean getaway',
        desc: 'Bring a heist home without the alarm ever rising.',
      },
      legend_job: { name: 'A legend’s job', desc: 'Win a heist on Legend difficulty.' },
    },
    cosmetic: {
      hat_tophat: 'Top hat',
      hat_panama: 'Panama hat',
      hat_hood: 'Hood',
      mask_fox: 'Fox mask',
      mask_skull: 'Skull mask',
      coat_crimson: 'Crimson coat',
      coat_ivory: 'Ivory coat',
      rim_gold: 'Gold light',
      rim_ghost: 'Ghost light',
      suit_crown: 'Crown',
      blueprint_sepia: 'Sepia blueprint',
      blueprint_noir: 'Noir blueprint',
      emote_cigar: 'Cigar',
      emote_watch: 'Pocket watch',
      emote_glass: 'Raised glass',
      emote_magnifier: 'Magnifying glass',
      car_coupe: 'Coupé',
      car_taxi: 'Taxi',
      car_roadster: 'Roadster',
    },
    parts: {
      hat: 'Hat',
      mask: 'Mask',
      coat: 'Coat',
      rim: 'Light',
      suit: 'Emblem',
      blueprint: 'Blueprint',
      emote: 'Emotes',
      car: 'Getaway car',
    },
    unlock: {
      level: 'Reach level {{level}}.',
      trophy: 'Earn the trophy “{{name}}”.',
      league: 'Reach the {{league}} league in Ranked.',
    },
    collection: {
      title: 'Collection',
      intro: 'Everything here is earned by playing. Nothing is for sale.',
      owned: '{{owned}} of {{total}} unlocked',
      equip: 'Wear',
      equipped: 'Worn',
      unequip: 'Take off',
      locked: 'Locked',
      unlocked: 'Unlocked',
      free: 'Free',
      classic: 'Classic blueprint',
      classicCar: 'Sedan',
      toProfile: 'Change your look',
      saved: 'Saved.',
      failed: 'It did not work. Try again.',
      blueprintHint: 'The look of the plan you draw and read before a heist.',
      emoteHint:
        'Sent to the table from the chat, in a heist or in a room. Four are free from the start.',
      carHint: 'The car you drive in the escape. Only you see it.',
    },
    leaderboards: {
      title: 'Leaderboards',
      thief: 'Thief rating',
      infiltrator: 'Mole rating',
      xp: 'Experience',
      season: 'Season {{n}}',
      seasonHint: 'Only players past their five placement games are listed.',
      empty: 'Nobody is on this board yet.',
      position: 'Rank',
      player: 'Player',
      rating: 'Rating',
      games: '{{games}} games',
      level: 'Level {{level}}',
      you: 'you',
      youOutside: 'You are not on this board yet.',
      tabs: 'Leaderboards',
      tabThief: 'Thief',
      tabInfiltrator: 'Mole',
      tabXp: 'Experience',
    },
    daily: {
      title: 'Daily heist',
      intro:
        'One heist a day, the same for everybody: the same map and the same role, and three attempts. The best counts: most loot, then the quicker.',
      today: 'Today’s heist',
      map: 'Map',
      role: 'Your role',
      difficulty: 'Difficulty',
      notReady: 'Today’s heist is not ready yet. Try again in a moment.',
      attempts: 'Your attempts',
      attemptLine: 'Attempt {{n}}',
      attemptsLeft_one: '{{count}} attempt left today.',
      attemptsLeft_other: '{{count}} attempts left today.',
      attemptsNone: 'You have used the three attempts of today. Come back tomorrow.',
      left: 'Left before the end',
      failed: 'The heist failed',
      loot: '{{amount}} M lire',
      play: 'Play today’s heist',
      next: 'Next heist in {{hours}} h {{minutes}} min',
      tomorrow: 'Tomorrow: {{map}}, as the {{role}}',
      board: 'Today’s board',
      boardEmpty: 'Nobody has brought the heist home yet.',
      boardHint: 'Guests play, but are not on the board.',
      timeUnit: '{{seconds}} s',
      you: 'you',
      starting: 'Opening the table…',
    },
    news: {
      title: 'News',
      empty: 'No news yet.',
      off: 'The news page is switched off for now.',
      pinned: 'Pinned',
    },
    settlement: {
      title: 'What the heist did for you',
      xp: '+{{xp}} XP',
      level: 'Level {{level}}',
      levelUp: 'Level up! Level {{level}}',
      breakdown: 'Where the XP comes from',
      participation: 'Taking part',
      success: 'The heist was a success',
      win: 'You won',
      unmask: 'You named the real Mole',
      infiltratorWin: 'The Mole wins',
      firstOfDay: 'First heist of the day, doubled',
      challenges: 'Challenges',
      challengeDone: 'Challenge done: +{{xp}} XP',
      trophies: 'New trophies',
      cosmetics: 'New in the collection',
      rating: 'Rating {{before}} → {{after}}',
      placing: 'Placement games: {{games}} of {{total}}',
      none: 'This heist did not count for you.',
      toRapSheet: 'Open the rap sheet',
      replay: 'Watch it again',
    },
    replay: {
      title: 'Replay',
      off: 'Replays are switched off for now.',
      tag: 'Replay',
      controls: 'Replay controls',
      play: 'Play',
      pause: 'Pause',
      time: 'Time in the heist',
      speed: 'Speed',
      watchAs: 'Watch as',
      bot: 'Bot ({{seat}})',
      notFound:
        'This replay is not available. Only the players who sat at a heist can watch it again, and only if the game server recorded it.',
      oldRules:
        'This heist was played with other rules (version {{version}}) and this version of the game cannot show it.',
    },
  },
};
